(() => {
  if (customElements.get('dashea-glass-header')) return;

  class DasheaGlassHeader extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.rail = this.querySelector('[data-dgh-rail]');
      this.bar = this.querySelector('[data-dgh-bar]');
      this.panel = this.querySelector('[data-dgh-panel]');
      this.toggle = this.querySelector('[data-dgh-toggle]');
      this.shop = this.querySelector('[data-dgh-shop]');
      if (!this.rail || !this.bar || !this.panel || !this.toggle) return;
      this.scrollHost = this.closest('[data-dgh-preview]')?.querySelector('[data-dgh-scroll]') || window;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.lastY = this.getScrollY();
      this.offset = 0;
      this.upward = 0;
      this.frame = 0;
      this.opened = false;
      this.panel.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.setAttribute('aria-label', 'Otevřít menu');

      this.toggle.addEventListener('click', () => this.setOpen(!this.opened, this.toggle), { signal });
      this.shop?.addEventListener('click', event => {
        event.preventDefault();
        this.setOpen(!this.opened, this.shop);
      }, { signal });
      this.panel.addEventListener('click', event => {
        const link = event.target.closest('a');
        if (!link) return;
        if (link.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
        this.setOpen(false);
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (this.opened && !this.contains(event.target)) this.setOpen(false);
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.opened) {
          event.preventDefault();
          const trigger = this.openTrigger || this.toggle;
          this.setOpen(false);
          trigger.focus();
        }
      }, { signal });
      this.addEventListener('focusin', () => this.reveal(), { signal });
      this.addEventListener('focusout', event => {
        if (this.opened && event.relatedTarget && !this.contains(event.relatedTarget)) this.setOpen(false);
      }, { signal });
      this.scrollHost.addEventListener('scroll', () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => this.onScroll());
      }, { passive: true, signal });
      // Call this event with { detail: { itemCount: cart.item_count } } after AJAX cart changes.
      document.addEventListener('dashea:cart-update', event => this.updateCart(event.detail?.itemCount), { signal });
      if (this.hasAttribute('data-open-on-load')) this.setOpen(true);
    }

    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
      this.controller = null;
    }

    getScrollY() {
      const element = this.scrollHost === window ? document.documentElement : this.scrollHost;
      const current = this.scrollHost === window ? window.scrollY : element.scrollTop;
      const limit = Math.max(0, element.scrollHeight - (this.scrollHost === window ? window.innerHeight : element.clientHeight));
      return Math.min(limit, Math.max(0, current));
    }

    setOpen(open, trigger) {
      this.opened = open;
      this.panel.hidden = !open;
      this.classList.toggle('is-open', open);
      this.toggle.setAttribute('aria-expanded', String(open));
      this.toggle.setAttribute('aria-label', open ? 'Zavřít menu' : 'Otevřít menu');
      this.shop?.setAttribute('aria-expanded', String(open));
      if (trigger) this.openTrigger = trigger;
      if (open) this.reveal();
      this.lastY = this.getScrollY();
      this.dispatchEvent(new CustomEvent('dashea:menu-toggle', { bubbles: true, detail: { open } }));
    }

    reveal() {
      this.offset = 0;
      this.rail.style.transition = this.reduced.matches ? 'none' : 'transform 220ms cubic-bezier(.22,1,.36,1)';
      this.rail.style.transform = 'translateY(0)';
    }

    onScroll() {
      this.frame = 0;
      const y = this.getScrollY();
      const delta = y - this.lastY;
      this.lastY = y;
      if (this.opened || this.querySelector(':focus-visible') || y <= 4) {
        this.upward = 0;
        this.reveal();
        return;
      }
      if (delta > 0) {
        this.upward = 0;
        const travel = this.bar.offsetHeight + (parseFloat(getComputedStyle(this).top) || 12) + 16;
        this.offset = Math.min(travel, this.offset + delta);
        this.rail.style.transition = 'none';
        this.rail.style.transform = `translateY(-${this.offset}px)`;
      } else if (delta < 0) {
        this.upward += -delta;
        if (this.upward >= 10 || y < 18) this.reveal();
      }
    }

    updateCart(count) {
      if (!Number.isInteger(count) || count < 0) return;
      const badge = this.querySelector('[data-dgh-count]');
      const link = this.querySelector('[data-dgh-cart]');
      if (badge) { badge.textContent = count > 99 ? '99+' : String(count); badge.hidden = count === 0; }
      if (link) link.setAttribute('aria-label', `Otevřít košík, počet položek: ${count}`);
    }
  }
  customElements.define('dashea-glass-header', DasheaGlassHeader);
})();
